import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Sparkles, Loader2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { formatApiError } from "@/lib/api";
import { PasswordInput } from "@/components/PasswordInput";
import { MembershipBenefits, useMembershipPriceLabel } from "@/components/MembershipBenefits";
import { toast } from "sonner";

export default function Register() {
  const { register } = useAuth();
  const nav = useNavigate();
  const [params] = useSearchParams();
  const initialRole = params.get("role") === "community" ? "community" : "discovery";
  const communityPrice = useMembershipPriceLabel("community");
  const discoveryPrice = useMembershipPriceLabel("discovery");
  const [form, setForm] = useState({ name: "", last_name: "", email: "", phone: "", city: "", state: "", password: "", role: initialRole });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await register(form);
      toast.success("Account created — welcome to the ecosystem.");
      nav("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-[80vh] grid place-items-center px-6 py-16">
      <div className="w-full max-w-md eyw-card p-10" data-testid="register-page">
        <span className="grid place-items-center h-12 w-12 rounded-full bg-[#144F75] text-white mb-6"><Sparkles size={20} /></span>
        <p className="eyw-label mb-1">Create your account</p>
        <h1 className="font-serif text-3xl text-[#1A242B] mb-6">Join the ecosystem.</h1>
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <button type="button" onClick={() => setForm((f) => ({ ...f, role: "discovery" }))}
              data-testid="register-role-discovery"
              className={`rounded-lg border px-3 py-3 text-sm ${form.role === "discovery" ? "border-[#144F75] bg-[#144F75]/5 text-[#144F75] font-semibold" : "border-[#E5E0D8] text-[#4A5D6B]"}`}>
              Discovery{discoveryPrice ? ` · ${discoveryPrice}` : ""}
            </button>
            <button type="button" onClick={() => setForm((f) => ({ ...f, role: "community" }))}
              data-testid="register-role-community"
              className={`rounded-lg border px-3 py-3 text-sm ${form.role === "community" ? "border-[#144F75] bg-[#144F75]/5 text-[#144F75] font-semibold" : "border-[#E5E0D8] text-[#4A5D6B]"}`}>
              Community{communityPrice ? ` · ${communityPrice}` : ""}
            </button>
          </div>
          <MembershipBenefits plan={form.role} />
          <div className="grid grid-cols-2 gap-3">
            <input required placeholder="First name" value={form.name} onChange={set("name")} data-testid="register-name" className="eyw-input w-full" />
            <input required placeholder="Last name" value={form.last_name} onChange={set("last_name")} data-testid="register-lastname" className="eyw-input w-full" />
          </div>
          <input required type="email" placeholder="Email" value={form.email} onChange={set("email")} data-testid="register-email" className="eyw-input w-full" />
          <input placeholder="Phone (optional)" value={form.phone} onChange={set("phone")} data-testid="register-phone" className="eyw-input w-full" />
          <div className="grid grid-cols-2 gap-3">
            <input placeholder="City (optional)" value={form.city} onChange={set("city")} data-testid="register-city" className="eyw-input w-full" />
            <input placeholder="State (optional)" value={form.state} onChange={set("state")} data-testid="register-state" className="eyw-input w-full" />
          </div>
          <PasswordInput value={form.password} onChange={set("password")} placeholder="Password (min 6 characters)" testid="register-password" required minLength={6} />
          <label className="flex items-start gap-2 text-xs text-[#4A5D6B] cursor-pointer">
            <input required type="checkbox" checked={form.accepted_terms || false}
              onChange={(e) => setForm((f) => ({ ...f, accepted_terms: e.target.checked }))}
              data-testid="register-terms-checkbox" className="mt-0.5 accent-[#144F75]" />
            <span>I agree to the <Link to="/terms" target="_blank" className="text-[#144F75] underline">Terms of Service</Link> and <Link to="/privacy" target="_blank" className="text-[#144F75] underline">Privacy Policy</Link>.</span>
          </label>
          <p className="text-xs text-[#8FA89B]">You can add more details anytime in Profile &amp; Privacy.</p>
          {error && <p className="text-sm text-red-600" data-testid="register-error">{error}</p>}
          <button type="submit" disabled={loading} data-testid="register-submit" className="eyw-btn w-full disabled:opacity-60">
            {loading && <Loader2 className="animate-spin" size={18} />} Create Account
          </button>
        </form>
        <p className="text-sm text-[#4A5D6B] mt-6">Already a member? <Link to="/login" className="text-[#144F75] font-medium">Login</Link></p>
      </div>
    </div>
  );
}
